import { useEffect, RefObject } from 'react';

// Matches the WAI-ARIA APG dialog/menu pattern: move focus into the overlay
// on open, trap Tab within it while open, and restore focus to whatever
// triggered it on close. Shared by every portaled overlay (Modal,
// MobileMenu, GameSelector/NotificationPanel/UserMenuPortal dropdowns,
// DateTimePicker) since none of them managed focus before this.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useOverlayFocus(containerRef: RefObject<HTMLElement | null>, open: boolean): void {
  useEffect(() => {
    if (!open) return undefined;

    const container = containerRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusable = () =>
      container ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : [];

    // Move focus into the overlay once it's mounted
    const raf = requestAnimationFrame(() => {
      const focusable = getFocusable();
      (focusable[0] || container)?.focus();
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !container) return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, containerRef]);
}
